import type { PostHogInterface } from 'posthog-js'

/**
 * Returns a guard that lets an installer run at most once per PostHog instance.
 * Each installer gets its own set, so installing one doesn't block the others.
 */
export function createInstallGuard(): (posthog: PostHogInterface) => boolean {
  const installed = new WeakSet<PostHogInterface>()
  return (posthog) => {
    if (installed.has(posthog)) return false
    installed.add(posthog)
    return true
  }
}

/** Number of enabled flags in an `onFeatureFlags` payload. */
export function countFlags(flags: unknown): number {
  return Array.isArray(flags) ? flags.length : 0
}

export type FlagFetchTrigger = 'init' | 'identify' | 'group' | 'reload' | 'reset'

const TRIGGER_METHODS: {
  method: 'identify' | 'group' | 'reloadFeatureFlags' | 'reset'
  trigger: FlagFetchTrigger
}[] = [
  { method: 'identify', trigger: 'identify' },
  { method: 'group', trigger: 'group' },
  { method: 'reloadFeatureFlags', trigger: 'reload' },
  { method: 'reset', trigger: 'reset' },
]

const listenersByInstance = new WeakMap<
  PostHogInterface,
  Set<(trigger: FlagFetchTrigger) => void>
>()

/**
 * Calls `listener` whenever the app asks PostHog to refetch flags.
 * The SDK methods are monkey-patched once per instance and shared by all
 * consumers, so multiple installs don't stack wrappers.
 */
export function onFlagFetchTrigger(
  posthog: PostHogInterface,
  listener: (trigger: FlagFetchTrigger) => void,
): void {
  const existing = listenersByInstance.get(posthog)
  if (existing) {
    existing.add(listener)
    return
  }
  const listeners = new Set([listener])
  listenersByInstance.set(posthog, listeners)

  // These SDK methods are not on PostHog's public type surface.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const instance = posthog as any
  for (const { method, trigger } of TRIGGER_METHODS) {
    const original = instance[method] as ((...args: unknown[]) => unknown) | undefined
    if (typeof original !== 'function') continue
    instance[method] = (...args: unknown[]) => {
      listeners.forEach((notify) => notify(trigger))
      return original.apply(posthog, args)
    }
  }
}
